import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, LinearProgress, Link,
  Radio, RadioGroup, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import KeyIcon from '@mui/icons-material/VpnKey';
import { Helmet } from 'react-helmet-async';
import {
  AccountUsage, ApiKeyInfo, CreatedApiKey, KeyMode, developerService, errorMessage,
} from '../../services/developerService';
import {
  FREE_MONTHLY_CALCS, KEY_PLACEHOLDER, MCP_URL, PIPX_INSTALL, SnippetLang, calculateSnippet,
  claudeCodeCommand, mcpHostedConfig, mcpStdioConfig, upgradeMailto,
} from './snippets';
import { copyText } from './copyText';
import { AppPage } from '../../components/app/AppPage';

const fmt = (n: number) => n.toLocaleString('en-US');

export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** The month resets at 00:00 UTC on the 1st; show that calendar date, not the local one. */
export const formatResetDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied' : label}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={async () => {
          if (await copyText(text)) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          }
        }}
      >
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const CodeBlock: React.FC<{ code: string; label: string }> = ({ code, label }) => (
  <Box sx={{ position: 'relative', bgcolor: 'var(--color-ink)', color: 'var(--color-paper)', borderRadius: 1, p: 2, pr: 6, overflowX: 'auto' }}>
    <Box sx={{ position: 'absolute', top: 6, right: 6, '& .MuiIconButton-root': { color: 'var(--color-paper-3)' } }}>
      <CopyButton text={code} label={`Copy ${label}`} />
    </Box>
    <Box component="pre" sx={{ m: 0, fontFamily: '"Red Hat Mono", ui-monospace, monospace', fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre' }}>
      <code>{code}</code>
    </Box>
  </Box>
);

export const UsageMeter: React.FC<{ usage: AccountUsage }> = ({ usage }) => {
  const live = usage.live;
  const free = live.free_limit;
  const used = live.calls;
  const pct = free > 0 ? Math.min(100, Math.round((Math.min(used, free) / free) * 100)) : 100;
  const exhausted = !usage.billing_enabled && live.remaining_free === 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="h6" component="h2">This month’s usage</Typography>
        <Typography variant="body2" color="text.secondary">Resets {formatResetDate(usage.period_resets_at)}</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={exhausted ? 'error' : pct >= 80 ? 'warning' : 'primary'}
        sx={{ height: 8, borderRadius: 4 }}
        aria-label="Live calculations used this month"
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 2 }}>
        <Box><Typography variant="caption" color="text.secondary">Free per month</Typography><Typography variant="h6" data-testid="usage-free">{fmt(free)}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Used</Typography><Typography variant="h6" data-testid="usage-used">{fmt(used)}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Remaining free</Typography><Typography variant="h6" data-testid="usage-remaining">{fmt(live.remaining_free)}</Typography></Box>
        {usage.billing_enabled && (
          <Box><Typography variant="caption" color="text.secondary">Paid overage</Typography><Typography variant="h6">{fmt(live.overage_calls)}</Typography></Box>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Live keys count <code>/calculate</code> and <code>/rates</code> calls. Nexus checks, usage lookups and idempotent replays are free.
        Test keys: {fmt(usage.test.calls)} of {fmt(usage.test.free_limit)} this month, never billed.
      </Typography>
      {exhausted && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          You’ve used this month’s free calculations. Live calls return 402 until {formatResetDate(usage.period_resets_at)} unless you upgrade to paid usage.
        </Alert>
      )}
    </Box>
  );
};

const DeveloperConsole: React.FC = () => {
  const [keys, setKeys] = useState<ApiKeyInfo[] | null>(null);
  const [usage, setUsage] = useState<AccountUsage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMode, setNewMode] = useState<KeyMode>('test');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<ApiKeyInfo | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const [lang, setLang] = useState<SnippetLang>('curl');
  const [mcpTab, setMcpTab] = useState<'hosted' | 'claude' | 'stdio'>('hosted');
  const [snippetKey, setSnippetKey] = useState<string>(KEY_PLACEHOLDER);
  const [checkingFirstCall, setCheckingFirstCall] = useState(false);
  const [noCallYet, setNoCallYet] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [k, u] = await Promise.all([developerService.listKeys(), developerService.usage()]);
      setKeys(k);
      setUsage(u);
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load your API keys.'));
      setKeys((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async () => {
    const name = newName.trim() || (newMode === 'live' ? 'Live key' : 'Test key');
    if (name.length > 100) {
      setCreateError('Name must be 100 characters or fewer.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const key = await developerService.createKey(name, newMode);
      setCreated(key);
      setSnippetKey(key.key);
      setCreateOpen(false);
      setNewName('');
      load();
    } catch (err) {
      setCreateError(errorMessage(err, 'Could not create the key.'));
    } finally {
      setCreating(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await developerService.revokeKey(revokeTarget.id);
      setRevokeTarget(null);
      load();
    } catch (err) {
      setRevokeError(errorMessage(err, 'Could not revoke the key.'));
    } finally {
      setRevoking(false);
    }
  };

  const activeKeys = (keys || []).filter((k) => k.status === 'active');
  const revokedKeys = (keys || []).filter((k) => k.status === 'revoked');
  const callsThisMonth = usage ? usage.test.calls + usage.live.calls : 0;

  const checkFirstCall = async () => {
    setCheckingFirstCall(true);
    setNoCallYet(false);
    try {
      const next = await developerService.usage();
      setUsage(next);
      setNoCallYet(next.test.calls + next.live.calls === 0);
    } catch {
      setNoCallYet(false);
    } finally {
      setCheckingFirstCall(false);
    }
  };

  const mcpCode = mcpTab === 'hosted' ? mcpHostedConfig(snippetKey) : mcpTab === 'claude' ? claudeCodeCommand(snippetKey) : `${PIPX_INSTALL}\n\n${mcpStdioConfig(snippetKey)}`;

  return (
    <AppPage
      title="Developers"
      description={<>API keys for the GoTruTax sales tax API and MCP server. {fmt(FREE_MONTHLY_CALCS)} live calculations a month are free.</>}
      actions={
        <>
          <Button component={RouterLink} to="/developers" variant="outlined">API docs</Button>
          <Button href="/docs" target="_blank" rel="noopener" variant="text">OpenAPI</Button>
        </>
      }
    >
      <Helmet>
        <title>Developers | GoTruTax</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {loadError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{loadError}</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          {usage ? <UsageMeter usage={usage} /> : !loadError ? <LinearProgress aria-label="Loading usage" /> : null}
          {usage && !usage.billing_enabled && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                Need more than {fmt(usage.live.free_limit)} calculations a month? Paid usage keeps live calls running past the free tier.
              </Typography>
              <Button variant="contained" href={upgradeMailto(usage.account)}>Upgrade to paid usage</Button>
            </Stack>
          )}
          {usage?.billing_enabled && (
            <Alert severity="success" sx={{ mt: 2 }}>Paid usage is on for this account. Calls past the free tier continue and are billed as overage.</Alert>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" component="h2">API keys</Typography>
            <Button variant="contained" data-testid="create-key" startIcon={<KeyIcon />} onClick={() => { setCreateError(null); setCreateOpen(true); }}>
              Create key
            </Button>
          </Stack>

          {created && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCreated(null)} data-testid="new-key-alert">
              <Typography fontWeight={600} gutterBottom>Copy your new {created.mode} key now. You won’t be able to see it again.</Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ bgcolor: 'background.paper', borderRadius: 1, px: 1.5, py: 0.5, border: 1, borderColor: 'divider' }}>
                <Box component="code" sx={{ fontFamily: '"Red Hat Mono", ui-monospace, monospace', wordBreak: 'break-all', flex: 1 }} data-testid="new-key-value">{created.key}</Box>
                <CopyButton text={created.key} label="Copy API key" />
              </Stack>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Store it in a secret manager or environment variable. We only keep a hash; if you lose it, revoke it and create another.
              </Typography>
            </Alert>
          )}

          {keys === null ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : keys.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No keys yet. Start with a test key: it runs on the GoTruTax engine and is never billed.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" aria-label="API keys">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last used</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...activeKeys, ...revokedKeys].map((k) => (
                    <TableRow key={k.id} sx={k.status === 'revoked' ? { opacity: 0.55 } : undefined}>
                      <TableCell>{k.name}</TableCell>
                      <TableCell><Chip size="small" label={k.mode} color={k.mode === 'live' ? 'primary' : 'default'} variant={k.mode === 'live' ? 'filled' : 'outlined'} /></TableCell>
                      <TableCell><Box component="code" sx={{ fontFamily: '"Red Hat Mono", ui-monospace, monospace', fontSize: 13 }}>{k.key_prefix}…</Box></TableCell>
                      <TableCell>{formatDate(k.created_at)}</TableCell>
                      <TableCell>{k.last_used_at ? formatDate(k.last_used_at) : 'Never'}</TableCell>
                      <TableCell>{k.status === 'active' ? 'Active' : `Revoked ${formatDate(k.revoked_at)}`}</TableCell>
                      <TableCell align="right">
                        {k.status === 'active' && (
                          <Button size="small" color="error" onClick={() => { setRevokeError(null); setRevokeTarget(k); }} aria-label={`Revoke ${k.name}`}>
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }} data-testid="quick-start">
        <CardContent>
          <Typography variant="h6" component="h2">Send your first call</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Amounts are integer cents. Send an <code>Idempotency-Key</code> so a retried order is never billed twice.
            {created
              ? ' The snippets use the key you just created.'
              : activeKeys.length
                ? ' Replace gtt_test_YOUR_KEY with your full key. Keys are shown once; if you lost it, create another.'
                : ' Create a test key first — it runs on the full engine and is never billed.'}
          </Typography>

          {usage && callsThisMonth === 0 && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="first-call-pending">
              No billable calls yet. Paste the command below in a terminal, then check back here.
            </Alert>
          )}
          {usage && callsThisMonth > 0 && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              data-testid="first-call-received"
              action={<Button color="inherit" size="small" component={RouterLink} to="/dashboard">Dashboard</Button>}
            >
              Your first calculation landed. {fmt(usage.test.calls)} test and {fmt(usage.live.calls)} live calls this
              month.
            </Alert>
          )}
          {noCallYet && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="first-call-none">
              Nothing yet. Check that the command used your full key and printed a JSON body, then check again.
            </Alert>
          )}

          <Tabs value={lang} onChange={(_, v) => setLang(v)} aria-label="Code sample language" sx={{ mb: 1 }}>
            <Tab value="curl" label="curl" />
            <Tab value="python" label="Python" />
            <Tab value="node" label="Node.js" />
          </Tabs>
          <CodeBlock code={calculateSnippet(lang, snippetKey)} label={`${lang} sample`} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={checkFirstCall} disabled={checkingFirstCall || !activeKeys.length}>
              {checkingFirstCall ? 'Checking…' : 'Check for my first call'}
            </Button>
            {!activeKeys.length && (
              <Button variant="contained" onClick={() => { setCreateError(null); setCreateOpen(true); }}>
                Create a test key
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" component="h2">Connect an AI agent (MCP)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The hosted server at <code>{MCP_URL}</code> gives agents four tools: calculate_sales_tax, get_tax_rates, check_nexus and get_usage.
            They use the same key, quota and limits as the REST API.
          </Typography>
          <Tabs value={mcpTab} onChange={(_, v) => setMcpTab(v)} aria-label="MCP client" sx={{ mb: 1 }}>
            <Tab value="hosted" label="Hosted (Cursor, Claude Desktop…)" />
            <Tab value="claude" label="Claude Code" />
            <Tab value="stdio" label="Local stdio (pipx)" />
          </Tabs>
          <CodeBlock code={mcpCode} label="MCP config" />
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create an API key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth margin="dense" label="Name" placeholder="e.g. Checkout service"
            value={newName} onChange={(e) => setNewName(e.target.value)} inputProps={{ maxLength: 100 }}
          />
          <RadioGroup value={newMode} onChange={(e) => setNewMode(e.target.value as KeyMode)} sx={{ mt: 1 }}>
            <FormControlLabel value="test" control={<Radio />} label={<span><strong>Test</strong> — GoTruTax engine, never billed. For development.</span>} />
            <FormControlLabel value="live" control={<Radio />} label={<span><strong>Live</strong> — production calculations; counts toward the {fmt(FREE_MONTHLY_CALCS)} free per month.</span>} />
          </RadioGroup>
          {createError && <Alert severity="error" sx={{ mt: 1 }}>{createError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={creating}>{creating ? 'Creating…' : 'Create key'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(revokeTarget)} onClose={() => !revoking && setRevokeTarget(null)}>
        <DialogTitle>Revoke “{revokeTarget?.name}”?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Requests using <code>{revokeTarget?.key_prefix}…</code> will fail with 401 immediately. This can’t be undone.
          </DialogContentText>
          {revokeError && <Alert severity="error" sx={{ mt: 2 }}>{revokeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRevoke} disabled={revoking}>{revoking ? 'Revoking…' : 'Revoke key'}</Button>
        </DialogActions>
      </Dialog>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        Full reference: <Link component={RouterLink} to="/developers">gotrutax.com/developers</Link>.
      </Typography>
    </AppPage>
  );
};

export default DeveloperConsole;
